import React, { useId, useState } from "react";
import ReactSelect, {
  StylesConfig,
  createFilter,
  OnChangeValue,
  Props as ReactSelectProps,
  components as ReactSelectComponents,
  IndicatorsContainerProps,
  OptionProps,
  ControlProps,
} from "react-select";
import { ISelectOption } from "netbank-shared/src/libs/models/Select/SelectOption";
import rootStyles from "~views/pages/Root.scss";
import styles from "./Select.scss";
import makeAnimated from "react-select/animated";
import { Colors } from "~globalstyles/colors";
import errorIcon from "~assets/error.svg";
import { tx } from "netbank-shared/src/libs/i18n";
import { IInfoPopoverProps } from "../InfoPopover/InfoPopover";
import chevronRight from "~assets/chevron-right.svg";
import { observer } from "mobx-react";
import { useStores } from "netbank-shared/src/hooks";
import { HtmlContent } from "../HtmlContent/HtmlContent";
import { LabelRow } from "../LabelRow/LabelRow";

const selectStyles: StylesConfig<ISelectOption> = {
  control: (base, { menuIsOpen, isFocused, isDisabled }) => ({
    ...base,
    height: 48,
    borderWidth: 2,
    borderColor: Colors.boulder,
    backgroundColor: "initial",
    borderRadius: 8,
    padding: "0 16px",
    borderBottomLeftRadius: menuIsOpen ? 0 : 8,
    borderBottomRightRadius: menuIsOpen ? 0 : 8,
    transition: "borderBottomLeftRadius 0s, borderBottomRightRadius 0s",
    boxShadow: "none",
    ":hover": {
      borderColor: Colors.boulder,
    },
    ...(isFocused && {
      outline: "none",
    }),
    ...(isFocused &&
      !menuIsOpen && {
        outline: `2px solid ${Colors.blueOutline}`,
        outlineOffset: 4,
      }),
    cursor: "pointer",
    ...(isDisabled && {
      color: Colors.boulder,
      borderColor: Colors.boulder,
    }),
  }),
  option: (base, { isFocused, isSelected, isDisabled }) => ({
    ...base,
    borderRadius: 8,
    color: Colors.black,
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    ...(isFocused && {
      outline: `2px solid ${Colors.blueOutline}`,
      backgroundColor: undefined,
    }),
    ":hover": {
      backgroundColor: Colors.blackSqueeze,
      outline: "none",
    },
    ...(isSelected && {
      backgroundColor: Colors.mediumSky,
      outline: "none",
    }),
    ...(isDisabled && {
      ":hover": {
        backgroundColor: undefined,
      },
      color: Colors.boulder,
      cursor: undefined,
    }),
  }),
  menu: (base) => ({
    ...base,
    marginTop: 0,
    borderRadius: 8,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    boxShadow: "none",
    border: `2px solid ${Colors.boulder}`,
    borderTop: "none",
  }),
  menuList: (base) => ({
    ...base,
    padding: "8px",
    "&::-webkit-scrollbar": {
      width: 8,
    },
    "&::-webkit-scrollbar-track": {
      background: Colors.cornflowerBlue,
      borderRadius: 11,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: Colors.blackSqueeze,
      borderRadius: 11,
    },
  }),
  placeholder: (base) => ({
    ...base,
    color: Colors.boulder,
  }),
  valueContainer: (base) => ({
    ...base,
    boxShadow: "none",
    padding: 0,
    fontSize: "16px",
    fontWeight: 400,
    color: Colors.black,
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: Colors.blackSqueeze,
    paddingLeft: 8,
    paddingRight: 4,
    borderRadius: 22,
    justifyContent: "space-between",
    alignItems: "center",
    height: 38,
    ":hover": {
      backgroundColor: Colors.blackSqueeze,
    },
    ":hover div[role=button] div": {
      backgroundColor: Colors.boulder,
    },
  }),
  multiValueLabel: (base) => ({
    ...base,
    fontSize: "18px",
    fontWeight: "400",
    paddingRight: 8,
    color: Colors.black,
  }),
  multiValueRemove: (base) => ({
    ...base,
    cursor: "pointer",
    padding: 0,
    ":hover": {
      backgroundColor: "initial",
    },
  }),
};

export interface ISelectProps<IsMulti extends boolean>
  extends Pick<ReactSelectProps<ISelectOption, IsMulti>, "components" | "isMulti" | "value" | "placeholder"> {
  options?: ISelectOption[];
  label?: string;
  subtext?: string;
  icon?: string;
  infoPopover?: IInfoPopoverProps;
  error?: string;
  disabled?: boolean;
  suffix?: string;
  suffixLabel?: string;
  onChange?: (value: NonNullable<OnChangeValue<ISelectOption, IsMulti>>) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

export const Select = observer(
  <IsMulti extends boolean>({
    value,
    options,
    label,
    subtext,
    placeholder,
    infoPopover,
    icon,
    error,
    disabled,
    isMulti,
    components,
    suffix,
    suffixLabel,
    onChange,
    onOpen,
    onClose,
  }: ISelectProps<IsMulti>) => {
    const animatedComponents = makeAnimated();
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const { uiStore } = useStores();
    const id = useId();

    const sortedOptions = [...(options || [])].sort((a, b) => {
      const p1 = a.priority || 0;
      const p2 = b.priority || 0;
      return p2 - p1;
    });

    const wrapperClasses = [];

    if(disabled){
      wrapperClasses.push(styles.faded);
    }
    return (
      <div className={wrapperClasses.join(" ")}>
        <LabelRow label={label} infoPopover={infoPopover} htmlFor={id} />
        {subtext && <HtmlContent className={styles.subtext} html={subtext} />}
        <ReactSelect<ISelectOption, IsMulti>
          inputId={id}
          styles={selectStyles}
          className={menuIsOpen ? styles.open : undefined}
          components={{
            ...animatedComponents,
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null,
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            IndicatorsContainer,
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            Control,
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            Option,
            ...components,
          }}
          openMenuOnClick
          onMenuOpen={() => {
            setMenuIsOpen(true);
            onOpen?.();
          }}
          onMenuClose={() => {
            setMenuIsOpen(false);
            onClose?.();
          }}
          onChange={(v) => {
            setMenuIsOpen(false);
            if (v) {
              onChange?.(v);
            }
          }}
          filterOption={createFilter({
            ignoreCase: true,
            ignoreAccents: true,
            trim: false,
            matchFrom: "start",
          })}
          noOptionsMessage={() => tx("misc.multiSelectNoOptions")}
          options={sortedOptions}
          placeholder={placeholder}
          value={value}
          menuIsOpen={menuIsOpen}
          isDisabled={disabled}
          isOptionDisabled={(option) => Boolean(option.disabled)}
          blurInputOnSelect
          isMulti={isMulti}
          isSearchable={Boolean(isMulti)}
          // @ts-ignore used for IndicatorsContainer component
          suffix={suffix}
          // @ts-ignore used for Control component
          icon={icon}
        />
        {uiStore.isMobile && suffix && suffixLabel && (
          <div className={styles.mobileSuffix}>
            <span>{suffixLabel}</span>
            <span>{suffix}</span>
          </div>
        )}
        {error && (
          <div className={rootStyles.error}>
            <img src={errorIcon} alt="error-icon" />
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  }
);

const Control = <IsMulti extends boolean>({ children, ...props }: ControlProps<ISelectOption, IsMulti>) => {
  // @ts-ignore
  const { icon } = props.selectProps;

  return (
    <ReactSelectComponents.Control {...props}>
      {icon && <img src={icon} style={{ marginRight: 12 }} alt="" />}
      {children}
    </ReactSelectComponents.Control>
  );
};

const DropdownIndicator = () => {
  return <img className={styles.expandIcon} src={chevronRight} alt="" />;
};

const IndicatorsContainer = observer(
  <IsMulti extends boolean>(props: IndicatorsContainerProps<ISelectOption, IsMulti>) => {
    const { suffix, isDisabled } = props.selectProps as unknown as ReactSelectProps<IsMulti> &
      Pick<ISelectProps<IsMulti>, "suffix">;

    const { uiStore } = useStores();

    return (
      <ReactSelectComponents.IndicatorsContainer {...props}>
        {!uiStore.isMobile && <span className={styles.suffix}>{suffix}</span>}
        {!isDisabled && <DropdownIndicator />}
      </ReactSelectComponents.IndicatorsContainer>
    );
  }
);

const Option = ({ children, ...props }: OptionProps<ISelectOption>) => {
  const { suffix, valueSuffix, disabled, disabledText, icon, iconAlt } = props.data;

  return (
    <ReactSelectComponents.Option {...props}>
      <div className={styles.optionIconWrapper}>
        {icon && <img src={icon} alt={iconAlt} />}
        <div>
          {children}
          {valueSuffix}
          {disabled && disabledText && <div className={styles.disabledOptionText}>{disabledText}</div>}
        </div>
      </div>
      {suffix && <div>{suffix}</div>}
    </ReactSelectComponents.Option>
  );
};
